import { useCallback, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import axiosInstance from '../../../../axios/axiosInstance';
import apiEndpoints from '../../../../axios/api';
import usePermissions from '../../../../hook/usePermissions';
import { PERMISSIONS } from '../../../../consts/permissions';
import { formatIstDateTime } from '../../../../utils/helpers/istDate';

// Investment reminders: maturity (days before, then weekly once matured) and
// interest instalments (on the due day, then weekly until collected). Sent by
// the daily 9 AM job as an email and an in-app notification.

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const ROLE_OPTIONS = [
    { code: 'SOCIETY_ADMIN', label: 'Society Admin' },
    { code: 'SOCIETY_MANAGER', label: 'Society Manager' },
    { code: 'COMMITTEE_CHAIRMAN', label: 'Chairman' },
    { code: 'COMMITTEE_SECRETARY', label: 'Secretary' },
    { code: 'COMMITTEE_TREASURER', label: 'Treasurer' },
];
const PRESET_DAYS = [60, 30, 15, 7, 2, 1, 0];
const dayLabel = (d) => (d === 0 ? 'On the day' : `${d} day${d === 1 ? '' : 's'} before`);

const Toggle = ({ on, onChange, disabled }) => (
    <button
        type="button"
        onClick={() => !disabled && onChange(!on)}
        disabled={disabled}
        className={`tw-relative tw-inline-flex tw-h-5 tw-w-9 tw-flex-shrink-0 tw-rounded-full tw-transition-colors ${on ? 'tw-bg-rose-600' : 'tw-bg-gray-300'} disabled:tw-opacity-50`}
    >
        <span className={`tw-inline-block tw-h-4 tw-w-4 tw-mt-0.5 tw-rounded-full tw-bg-white tw-shadow tw-transition-transform ${on ? 'tw-translate-x-4' : 'tw-translate-x-0.5'}`} />
    </button>
);

const Row = ({ title, desc, children }) => (
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-p-3 tw-border tw-border-gray-100 tw-rounded-lg">
        <div>
            <p className="tw-text-sm tw-font-medium tw-text-gray-700">{title}</p>
            {desc && <p className="tw-text-[11px] tw-text-gray-500">{desc}</p>}
        </div>
        {children}
    </div>
);

const InvestmentRemindersModal = ({ onClose }) => {
    const { can } = usePermissions();
    const canEdit = can(PERMISSIONS.INVESTMENT_UPDATE);
    const [settings, setSettings] = useState(null);
    const [logs, setLogs] = useState([]);
    const [customDay, setCustomDay] = useState('');
    const [saving, setSaving] = useState(false);
    const [running, setRunning] = useState(false);

    const load = useCallback(async () => {
        try {
            const [s, l] = await Promise.all([
                axiosInstance.get(apiEndpoints.INVESTMENT_REMINDER_SETTINGS_API),
                axiosInstance.get(apiEndpoints.INVESTMENT_REMINDER_LOGS_API, { params: { limit: 20 } }),
            ]);
            setSettings(s?.data || null);
            setLogs(Array.isArray(l?.data) ? l.data : []);
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to load reminder settings');
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const set = (k, v) => setSettings(prev => ({ ...prev, [k]: v }));
    const toggleDay = (d) => set('days_before', settings.days_before.includes(d)
        ? settings.days_before.filter(x => x !== d)
        : [...settings.days_before, d].sort((a, b) => b - a));
    const addCustomDay = () => {
        const d = parseInt(customDay, 10);
        if (Number.isInteger(d) && d >= 0 && d <= 365 && !settings.days_before.includes(d)) toggleDay(d);
        setCustomDay('');
    };
    const toggleRole = (code) => set('notify_roles', settings.notify_roles.includes(code)
        ? settings.notify_roles.filter(r => r !== code)
        : [...settings.notify_roles, code]);

    const save = async () => {
        setSaving(true);
        try {
            const { saved, ...body } = settings;
            const res = await axiosInstance.put(apiEndpoints.INVESTMENT_REMINDER_SETTINGS_API, body);
            setSettings(res?.data || settings);
            toast.success('Reminder settings saved');
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const runNow = async () => {
        setRunning(true);
        try {
            const res = await axiosInstance.post(apiEndpoints.INVESTMENT_REMINDER_RUN_API, {});
            const r = res?.data || {};
            if (!r.enabled) toast.info('Reminders are switched off');
            else if (!r.due?.length) toast.info(r.skipped_already_sent ? 'Today\'s reminders were already sent' : 'Nothing is due a reminder today');
            else toast.success(`Sent ${r.due.length} reminder(s): ${r.emails_sent} email(s), ${r.in_app_sent} in-app`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to send reminders');
        } finally {
            setRunning(false);
        }
    };

    const allOff = settings && !settings.maturity_enabled && !settings.interest_enabled;
    const days = settings ? [...new Set([...PRESET_DAYS, ...settings.days_before])].sort((a, b) => b - a) : [];

    return (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/40 tw-p-4" onClick={onClose}>
            <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-xl tw-max-h-[90vh] tw-flex tw-flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-gray-100">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:bell-ring-outline" className="tw-text-xl tw-text-rose-600" />
                        <div>
                            <h2 className="tw-text-sm tw-font-bold tw-text-gray-800">Investment Reminders</h2>
                            <p className="tw-text-[11px] tw-text-gray-500">Email and in-app, sent daily at 9 AM</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="tw-p-1 tw-rounded-full hover:tw-bg-gray-100">
                        <Icon icon="mdi:close" className="tw-text-base tw-text-gray-400" />
                    </button>
                </div>

                {!settings ? (
                    <div className="tw-flex tw-justify-center tw-py-12">
                        <div className="tw-animate-spin tw-rounded-full tw-h-7 tw-w-7 tw-border-b-2 tw-border-rose-600"></div>
                    </div>
                ) : (
                    <div className="tw-p-5 tw-space-y-3 tw-overflow-y-auto">
                        <Row title="Maturity reminders" desc="Before an active investment matures">
                            <Toggle on={settings.maturity_enabled} onChange={v => set('maturity_enabled', v)} disabled={!canEdit} />
                        </Row>
                        {settings.maturity_enabled && (
                            <div className="tw-p-3 tw-border tw-border-gray-100 tw-rounded-lg">
                                <p className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Remind</p>
                                <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                                    {days.map(d => (
                                        <button
                                            key={d}
                                            type="button"
                                            onClick={() => canEdit && toggleDay(d)}
                                            disabled={!canEdit}
                                            className={`tw-px-2.5 tw-py-1 tw-rounded-full tw-text-xs tw-border ${settings.days_before.includes(d) ? 'tw-bg-rose-600 tw-border-rose-600 tw-text-white' : 'tw-bg-white tw-border-gray-200 tw-text-gray-600 hover:tw-bg-gray-50'}`}
                                        >
                                            {dayLabel(d)}
                                        </button>
                                    ))}
                                </div>
                                {canEdit && (
                                    <div className="tw-flex tw-items-center tw-gap-2 tw-mt-2 tw-text-xs tw-text-gray-600">
                                        <input type="number" min="0" max="365" value={customDay} onChange={e => setCustomDay(e.target.value)} placeholder="Days" className="tw-w-20 tw-px-2 tw-py-1 tw-border tw-border-gray-200 tw-rounded-md" />
                                        <button type="button" onClick={addCustomDay} className="tw-px-2.5 tw-py-1 tw-rounded-md tw-border tw-border-gray-200 hover:tw-bg-gray-50">Add</button>
                                    </div>
                                )}
                            </div>
                        )}
                        <Row title="Interest reminders" desc="When an interest instalment falls due and has not been collected">
                            <Toggle on={settings.interest_enabled} onChange={v => set('interest_enabled', v)} disabled={!canEdit} />
                        </Row>
                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-p-3 tw-border tw-border-gray-100 tw-rounded-lg">
                            <div>
                                <p className="tw-text-sm tw-font-medium tw-text-gray-700">Keep reminding weekly</p>
                                <p className="tw-text-[11px] tw-text-gray-500">While matured investments are not closed or renewed, and interest is not collected (up to 90 days)</p>
                            </div>
                            <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
                                <select value={settings.weekly_day} onChange={e => set('weekly_day', Number(e.target.value))} disabled={!canEdit || !settings.overdue_weekly || allOff} className="tw-px-2 tw-py-1 tw-border tw-border-gray-200 tw-rounded-md tw-text-xs">
                                    {WEEKDAYS.map((w, i) => <option key={w} value={i}>{w}</option>)}
                                </select>
                                <Toggle on={settings.overdue_weekly} onChange={v => set('overdue_weekly', v)} disabled={!canEdit || allOff} />
                            </div>
                        </div>

                        <div className="tw-p-3 tw-border tw-border-gray-100 tw-rounded-lg">
                            <p className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Who gets them</p>
                            <div className="tw-grid tw-grid-cols-2 tw-gap-1.5">
                                {ROLE_OPTIONS.map(r => (
                                    <label key={r.code} className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-700 tw-cursor-pointer">
                                        <input type="checkbox" checked={settings.notify_roles.includes(r.code)} onChange={() => toggleRole(r.code)} disabled={!canEdit || allOff} />
                                        {r.label}
                                    </label>
                                ))}
                            </div>
                            <textarea
                                rows={2}
                                value={settings.extra_emails || ''}
                                onChange={e => set('extra_emails', e.target.value)}
                                disabled={!canEdit || allOff}
                                placeholder="Other email addresses, comma-separated"
                                className="tw-w-full tw-mt-2 tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-xs"
                            />
                        </div>

                        {logs.length > 0 && (
                            <div className="tw-p-3 tw-border tw-border-gray-100 tw-rounded-lg">
                                <p className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Recently sent</p>
                                <ul className="tw-space-y-1">
                                    {logs.map(l => (
                                        <li key={l.id} className="tw-flex tw-justify-between tw-gap-2 tw-text-[11px] tw-text-gray-600">
                                            <span className="tw-truncate">{l.label} · {String(l.reminder_key).startsWith('I') ? 'Interest' : 'Maturity'}</span>
                                            <span className="tw-text-gray-400 tw-whitespace-nowrap">
                                                {formatIstDateTime(l.sent_at)} · {l.emails_sent} email(s)
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                {settings && canEdit && (
                    <div className="tw-flex tw-flex-wrap tw-justify-end tw-gap-2 tw-px-5 tw-py-3 tw-border-t tw-border-gray-100">
                        <button onClick={runNow} disabled={running || allOff} title="Send reminders that are due today, without waiting for the 9 AM run" className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-xs tw-font-medium tw-text-blue-700 tw-border tw-border-blue-200 hover:tw-bg-blue-50 disabled:tw-opacity-50">
                            <Icon icon={running ? 'mdi:loading' : 'mdi:send-outline'} className={running ? 'tw-animate-spin' : ''} />
                            Send due reminders now
                        </button>
                        <button onClick={save} disabled={saving} className="tw-px-4 tw-py-1.5 tw-rounded-lg tw-text-xs tw-font-medium tw-bg-rose-600 tw-text-white hover:tw-bg-rose-700 disabled:tw-opacity-50">
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InvestmentRemindersModal;
